import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IUser } from 'common_library';
import { AuthService } from '../../services/auth/auth.service';

/**
 * Modal (standalone) di selezione utente da impersonare — solo admin.
 * Cerca via endpoint admin-only; on-pick chiude ritornando l'utente scelto.
 */
@Component({
  selector: 'yf-impersonate-user-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TranslateModule],
  template: `
    <ion-header>
      <ion-toolbar color="warning">
        <ion-title>{{ 'PROFILE.IMPERSONATE.TITLE' | translate }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="cancel()">{{ 'PROFILE.IMPERSONATE.CANCEL' | translate }}</ion-button>
        </ion-buttons>
      </ion-toolbar>
      <ion-toolbar>
        <ion-searchbar
          [(ngModel)]="term"
          (ionInput)="search()"
          [debounce]="300"
          [placeholder]="'PROFILE.IMPERSONATE.SEARCH' | translate">
        </ion-searchbar>
      </ion-toolbar>
    </ion-header>
    <ion-content class="impersonate-content">
      @if (loading) {
        <div class="ion-text-center ion-padding"><ion-spinner></ion-spinner></div>
      }
      <ion-list class="impersonate-list" lines="none">
        @for (u of results; track u.id) {
          <ion-item button lines="none" class="impersonate-item" (click)="pick(u)">
            <ion-label>
              <h3>{{ u.surname }} {{ u.name }}</h3>
              <p>{{ u.email }}</p>
            </ion-label>
          </ion-item>
        }
      </ion-list>
      @if (!loading && term.trim().length >= 2 && results.length === 0) {
        <div class="ion-text-center ion-padding impersonate-empty">
          {{ 'PROFILE.IMPERSONATE.NO_RESULTS' | translate }}
        </div>
      }
    </ion-content>
  `,
  styles: [`
    .impersonate-content { --padding-start: 12px; --padding-end: 12px; --padding-top: 10px; --padding-bottom: 10px; --background: var(--ion-color-light, #f4f5f8); }
    .impersonate-list { background: transparent; padding: 0; }
    .impersonate-item {
      --background: var(--ion-background-color, #fff);
      --border-radius: 12px;
      --padding-start: 16px;
      --inner-padding-end: 12px;
      --min-height: 60px;
      margin-bottom: 10px;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
    .impersonate-item h3 { font-weight: 600; margin-bottom: 2px; }
    .impersonate-item p { color: var(--ion-color-medium, #92949c); font-size: 13px; }
    .impersonate-empty { color: var(--ion-color-medium, #92949c); margin-top: 24px; }
  `],
})
export class ImpersonateUserModalComponent {
  term = '';
  results: IUser[] = [];
  loading = false;

  constructor(
    private auth: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private translate: TranslateService,
  ) {}

  async search(): Promise<void> {
    const t = this.term?.trim() ?? '';
    if (t.length < 2) {
      this.results = [];
      return;
    }
    this.loading = true;
    this.results = await this.auth.searchUsers(t);
    this.loading = false;
  }

  /** Conferma prima di avviare l'impersonation; su annulla si resta nella lista. */
  async pick(u: IUser): Promise<void> {
    const name = `${u.surname ?? ''} ${u.name ?? ''}`.trim() || u.email;
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('PROFILE.IMPERSONATE.CONFIRM_TITLE'),
      message: this.translate.instant('PROFILE.IMPERSONATE.CONFIRM_MSG', { name, email: u.email }),
      buttons: [
        { text: this.translate.instant('PROFILE.IMPERSONATE.CANCEL'), role: 'cancel' },
        {
          text: this.translate.instant('PROFILE.IMPERSONATE.CONFIRM_OK'),
          role: 'confirm',
          handler: () => this.modalCtrl.dismiss(u, 'confirm'),
        },
      ],
    });
    await alert.present();
  }

  cancel(): void {
    this.modalCtrl.dismiss(null, 'cancel');
  }
}
